import { CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';

import { EPanelDirections } from '@/store/slices/layout-grid/types';
import Portal from '@ui/portal/portal';
import * as EventEmitter from '@/utils/event-emitter';

import styles from './fixed-drawer.module.css';

/*
  TODO:
  - height?: CSSProperties['height'];
  - onTransitionStart?: () => void;
  - onTransitionEnd?: () => void;
*/
type FixedDrawerProps = {
  id: string;
  children?: React.ReactNode;
  direction?: EPanelDirections;
  open?: boolean;
  fade?: boolean;
  underHeader?: boolean;
  backgroundColor?: string;
  trapFocus?: boolean;
  noFocusGuards?: boolean;
  focusGroup?: string;
};

/* This component works hand-in-hand with the useFixedDrawer hook */
const FixedDrawer = (props: FixedDrawerProps) => {
  const {
    children,
    direction,
    open: _open,
    id,
    fade,
    underHeader,
    backgroundColor,
    trapFocus,
    noFocusGuards,
    focusGroup,
  } = props;

  const [open, setOpen] = useState(!!_open);
  const closeTimeout = useRef<NodeJS.Timeout>(null);

  const portalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (_open !== undefined) {
      setOpen(_open);
    }
  }, [_open]);

  // broadcast event whether this modal is open/closed
  // we can then use the useIsModalOpen hook to detect if any modal is open by passing in the id to that hook
  useEffect(() => {
    EventEmitter.broadcastEvent(`${id}::fixed-drawer-open-state`, open);
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    if (!open) {
      EventEmitter.broadcastEvent(`${id}::fixed-drawer-closing-state`, true);
      closeTimeout.current = setTimeout(
        () => EventEmitter.broadcastEvent(`${id}::fixed-drawer-closing-state`, false),
        300
      );
    } else {
      EventEmitter.broadcastEvent(`${id}::fixed-drawer-closing-state`, false);
    }
  }, [id, open]);

  // listen for toggle requests
  useEffect(() => {
    const toggleDrawerId = `${id}::fixed-drawer-toggle`;
    const handleToggle = () => {
      setOpen((prev) => !prev);
    };
    EventEmitter.addEventListener(toggleDrawerId, handleToggle);

    return () => EventEmitter.removeEventListener(toggleDrawerId, handleToggle);
  }, [id]);

  // listen for open requests
  useEffect(() => {
    const openDrawerId = `${id}::fixed-drawer-open`;
    const handleOpen = () => {
      setOpen(true);
    };
    EventEmitter.addEventListener(openDrawerId, handleOpen);

    return () => EventEmitter.removeEventListener(openDrawerId, handleOpen);
  }, [id]);

  // listen for close requests
  useEffect(() => {
    const closeDrawerId = `${id}::fixed-drawer-close`;
    const handleClose = () => {
      setOpen(false);
    };
    EventEmitter.addEventListener(closeDrawerId, handleClose);

    return () => EventEmitter.removeEventListener(closeDrawerId, handleClose);
  }, [id]);

  const TrapWrapper = trapFocus ? FocusLock : Fragment;
  const trapProps = trapFocus
    ? {
        returnFocus: true,
        disabled: !open,
        ...(noFocusGuards ? { noFocusGuards: true } : {}),
        ...(focusGroup ? { group: focusGroup } : {}),
      }
    : {};

  return (
    <Portal
      className={`${styles['admin-fixed-drawer-portal']} ${direction ? styles[direction] : ''} fixed-drawer-id-${id} ${fade ? styles['fade'] : ''} ${underHeader ? styles['under-header'] : ''}`}
      style={{ '--fixed-drawer-background-color': backgroundColor || 'var(--BG)' } as CSSProperties}
      ref={portalRef}
    >
      <div
        id={id}
        data-testid="admin-fixed-drawer"
        className={`${styles['admin-fixed-drawer']} ${open ? styles['open'] : ''} ${direction ? styles[direction] : ''}`}
      >
        <TrapWrapper {...trapProps}>{children}</TrapWrapper>
      </div>
    </Portal>
  );
};

export default FixedDrawer;
